<template>
  <div class="intake-stats">
    <!-- Total Summary -->
    <div class="total-summary mb-3 pa-3">
      <div class="d-flex justify-space-between align-center mb-2">
        <h3 class="text-subtitle-1 font-weight-bold mb-0">Total Cases</h3>
        <div class="d-flex align-center">
          <v-icon :color="getTotalIconColor()" class="mr-1" small>{{
            getTotalIcon()
          }}</v-icon>
          <span
            class="text-subtitle-1 font-weight-bold"
            :class="getTotalTextClass()"
          >
            {{ goalPercentage(retainedCases) }}%
          </span>
        </div>
      </div>

      <div class="d-flex align-center justify-space-between">
        <div>
          <span class="text-h5 font-weight-bold">{{ retainedCases }}</span>
          <span class="text-body-2 ml-2 grey--text"
            >/ {{ retainedCasesGoal() }}</span
          >
        </div>
        <div class="text-caption" :class="getTotalTextClass()">
          <span v-if="retainedCases < retainedCasesGoal()">
            +{{ Math.ceil(catchUpRate(retainedCases)) }}/day needed
          </span>
          <span v-else>On track!</span>
        </div>
      </div>

      <v-progress-linear
        :value="Math.min(goalPercentage(retainedCases), 100)"
        :color="getTotalIconColor()"
        background-color="grey lighten-4"
        height="4"
        class="mt-2"
      ></v-progress-linear>
    </div>

    <!-- Office Stats Grid -->
    <v-row dense>
      <v-col
        cols="6"
        sm="4"
        md="3"
        v-for="(item, index) in retainedCasesInPeriodByOffice"
        :key="index"
      >
        <div class="office-stat pa-2">
          <div class="d-flex justify-space-between align-center mb-1">
            <div class="text-caption font-weight-bold">
              {{ getOfficeShortName(item.office) }}
            </div>
            <v-icon
              x-small
              :color="getOfficeIconColor(item.count, item.office)"
            >
              {{ getOfficeIcon(item.count, item.office) }}
            </v-icon>
          </div>

          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-h6 font-weight-bold">{{ item.count }}</span>
            <span
              class="text-caption font-weight-medium"
              :class="getOfficeTextClass(item.count, item.office)"
            >
              {{
                officeGoalPercentage(item.count, item.office).replace("%", "")
              }}%
            </span>
          </div>

          <div class="text-caption grey--text mb-1">
            {{ officeGoal(item.office) }} goal
          </div>

          <v-progress-linear
            :value="
              Math.min(
                parseFloat(
                  officeGoalPercentage(item.count, item.office).replace('%', '')
                ),
                100
              )
            "
            :color="getOfficeIconColor(item.count, item.office)"
            background-color="grey lighten-4"
            height="2"
            class="mb-1"
          ></v-progress-linear>

          <div
            class="text-caption"
            :class="getOfficeTextClass(item.count, item.office)"
          >
            <span v-if="item.count < officeGoal(item.office)">
              {{ Math.ceil(officeGap(item.count, item.office)) }} behind
            </span>
            <span v-else-if="item.count >= officeGoal(item.office) * 1.1">
              Exceeding!
            </span>
            <span v-else>
              On track
            </span>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import differenceInBusinessDays from "date-fns/differenceInBusinessDays";
import addDays from "date-fns/addDays";
import { mapGetters } from "vuex";
import gql from "graphql-tag";

export default {
  data() {
    return {
      search: "",
      headers: [
        { text: "Action", value: "caseId", width: "100px" },
        { text: "Type", value: "type" },
        { text: "State", value: "state" },
        { text: "Client", value: "clientName" },
        { text: "Attorney", value: "attorney" },
        { text: "Paralegal", value: "paralegal" },
        { text: "Opened", value: "dateOpened" },
        { text: "Closed", value: "dateClosed" },
        { text: "Reason", value: "reasonClosed" },
      ],
      closedCases: [],
      retainedOnly: false,
    };
  },
  computed: {
    ...mapGetters(["startDate", "endDate", "currentUser"]),
    retainedCases() {
      if (!this.organization) return 0;
      return this.organization.retainedCasesInPeriod;
    },
    businessDays() {
      let start = this.startDate.split("-").map((x) => parseInt(x));
      let startDate = new Date(start[0], start[1] - 1, start[2]);
      let end = this.endDate.split("-").map((x) => parseInt(x));
      let endDate = new Date(end[0], end[1] - 1, end[2]);
      // We only want to count business days up to today if range is current month
      // If historical range we will count business days through range
      if (endDate > new Date()) {
        endDate = new Date();
      }
      // Add a day to endDate so we can include the last day
      endDate = addDays(endDate, 1);

      return differenceInBusinessDays(endDate, startDate);
    },
    businessDaysRemaining() {
      let end = this.endDate.split("-").map((x) => parseInt(x));
      let endDate = new Date(end[0], end[1] - 1, end[2]);
      let today = new Date();

      if (endDate <= today) return 0;

      return differenceInBusinessDays(endDate, today);
    },
    retainedCasesInPeriodByOffice() {
      if (!this.organization) return [];
      return this.organization.retainedCasesInPeriodByOffice;
    },
    totalCardColor() {
      const percentage = this.goalPercentage(this.retainedCases);
      if (percentage >= 100) return "success";
      if (percentage >= 80) return "primary";
      if (percentage >= 60) return "warning";
      return "error";
    },
    totalPerformanceColor() {
      const percentage = this.goalPercentage(this.retainedCases);
      if (percentage >= 100) return "success";
      if (percentage >= 80) return "info";
      if (percentage >= 60) return "warning";
      return "error";
    },
  },
  methods: {
    retainedCasesGoal() {
      // Fixed monthly goal of 268 cases
      const monthlyGoal = 300;

      // Get the actual number of business days in the month being viewed
      let start = this.startDate.split("-").map((x) => parseInt(x));
      let startOfMonth = new Date(start[0], start[1] - 1, 1);
      let endOfMonth = new Date(start[0], start[1], 0); // Last day of month

      const businessDaysInMonth = differenceInBusinessDays(
        addDays(endOfMonth, 1),
        startOfMonth
      );

      // Calculate what fraction of the month this period represents
      const periodFraction = this.businessDays / businessDaysInMonth;

      // Apply fraction to monthly goal and round up
      return Math.ceil(monthlyGoal * periodFraction);
    },
    goalPercentage(retainedCases) {
      return (
        Math.round((retainedCases / this.retainedCasesGoal()) * 100 * 10) / 10
      );
    },
    officeGoal(office) {
      // Fixed monthly goals for each office (totaling 268)
      let monthlyOfficeGoal;
      switch (office) {
        case "(3 - ID Main) Meridian Office":
          // Boise office: 54 cases/month
          monthlyOfficeGoal = 54;
          break;
        case "(2 - WA Main) Spokane Valley Office":
          // Spokane Valley office: 92 cases/month
          monthlyOfficeGoal = 92;
          break;
        case "(1 - UT Main) Sandy Office":
          // Sandy office: 122 cases/month
          monthlyOfficeGoal = 143;
          break;
        case "Sea-Tac":
          // Sea-Tac office: 122 cases/month
          monthlyOfficeGoal = 14;
          break;
        case "Wyoming":
        case "Montana":
          // Secondary offices - minimal goals
          monthlyOfficeGoal = 0; // Can adjust if needed
          break;
        default:
          monthlyOfficeGoal = 0;
      }

      // Get the actual number of business days in the month being viewed
      let start = this.startDate.split("-").map((x) => parseInt(x));
      let startOfMonth = new Date(start[0], start[1] - 1, 1);
      let endOfMonth = new Date(start[0], start[1], 0); // Last day of month

      const businessDaysInMonth = differenceInBusinessDays(
        addDays(endOfMonth, 1),
        startOfMonth
      );

      // Calculate what fraction of the month this period represents
      const periodFraction = this.businessDays / businessDaysInMonth;

      // Apply fraction to monthly goal and round up
      return Math.ceil(monthlyOfficeGoal * periodFraction);
    },
    officeGoalPercentage(count, office) {
      const goal = this.officeGoal(office);
      if (goal === 0) return "0";
      return `${Math.round((count / goal) * 100 * 10) / 10}`;
    },
    getOfficeShortName(office) {
      switch (office) {
        case "(3 - ID Main) Meridian Office":
          return "Boise";
        case "(2 - WA Main) Spokane Valley Office":
          return "Spokane Valley";
        case "(1 - UT Main) Sandy Office":
          return "Sandy";
        case "Sea-Tac":
          return "Sea-Tac";
        case "Wyoming":
          return "Wyoming";
        case "Montana":
          return "Montana";
        default:
          return office;
      }
    },
    getOfficeCardColor(count, office) {
      const goal = this.officeGoal(office);
      const percentage = (count / goal) * 100;

      if (percentage >= 100) return "success";
      if (percentage >= 80) return "primary";
      if (percentage >= 60) return "warning";
      return "error";
    },
    getOfficePerformanceColor(count, office) {
      const goal = this.officeGoal(office);
      const percentage = (count / goal) * 100;

      if (percentage >= 100) return "success";
      if (percentage >= 80) return "info";
      if (percentage >= 60) return "warning";
      return "error";
    },
    officeGap(count, office) {
      const goal = this.officeGoal(office);
      return Math.max(0, goal - count);
    },
    officeCatchUpRate(count, office) {
      const gap = this.officeGap(count, office);
      const daysRemaining = Math.max(1, this.businessDaysRemaining);
      return gap / daysRemaining;
    },
    catchUpRate(current) {
      const goal = this.retainedCasesGoal();
      const gap = Math.max(0, goal - current);
      const daysRemaining = Math.max(1, this.businessDaysRemaining);
      return gap / daysRemaining;
    },
    // New styling methods
    getTotalTextClass() {
      const percentage = this.goalPercentage(this.retainedCases);
      if (percentage >= 100) return "success--text";
      if (percentage >= 80) return "primary--text";
      if (percentage >= 60) return "warning--text";
      return "error--text";
    },
    getTotalIconColor() {
      const percentage = this.goalPercentage(this.retainedCases);
      if (percentage >= 100) return "success";
      if (percentage >= 80) return "primary";
      if (percentage >= 60) return "warning";
      return "error";
    },
    getTotalIcon() {
      const percentage = this.goalPercentage(this.retainedCases);
      if (percentage >= 100) return "mdi-check-circle";
      if (percentage >= 80) return "mdi-trending-up";
      if (percentage >= 60) return "mdi-clock-outline";
      return "mdi-alert-circle";
    },
    getOfficeTextClass(count, office) {
      const goal = this.officeGoal(office);
      const percentage = (count / goal) * 100;

      if (percentage >= 100) return "success--text";
      if (percentage >= 80) return "primary--text";
      if (percentage >= 60) return "warning--text";
      return "error--text";
    },
    getOfficeIconColor(count, office) {
      const goal = this.officeGoal(office);
      const percentage = (count / goal) * 100;

      if (percentage >= 100) return "success";
      if (percentage >= 80) return "primary";
      if (percentage >= 60) return "warning";
      return "error";
    },
    getOfficeIcon(count, office) {
      const goal = this.officeGoal(office);
      const percentage = (count / goal) * 100;

      if (percentage >= 100) return "mdi-check-circle";
      if (percentage >= 80) return "mdi-trending-up";
      if (percentage >= 60) return "mdi-clock-outline";
      return "mdi-alert-circle";
    },
  },
  apollo: {
    organization: {
      query: gql`
        query OrgHomeIntakeStatsPacing(
          $id: ID!
          $startDate: String!
          $endDate: String!
        ) {
          organization(id: $id) {
            id
            retainedCasesInPeriod(startDate: $startDate, endDate: $endDate)
            retainedCasesInPeriodByOffice(
              startDate: $startDate
              endDate: $endDate
            ) {
              office
              count
            }
          }
        }
      `,
      variables() {
        return {
          id: this.currentUser.organization.id,
          startDate: this.startDate,
          endDate: this.endDate,
        };
      },
      skip() {
        return (
          this.startDate === null ||
          this.endDate === null ||
          this.currentUser === null
        );
      },
    },
  },
};
</script>

<style scoped>
.intake-stats {
  max-width: 100%;
}

.total-summary {
  border-radius: 8px;
  background-color: #fafafa;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: box-shadow 0.2s ease;
}

.total-summary:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.office-stat {
  border-radius: 6px;
  background-color: #fafafa;
  min-height: 120px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  transition: box-shadow 0.2s ease;
}

.office-stat:hover {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
}

@media (max-width: 600px) {
  .office-stat {
    min-height: 100px;
  }
}
</style>
